import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Button, Card, Col, Form, Input, Modal, Row, Tag, Typography, Upload, message } from 'antd';
import { BankOutlined, IdcardOutlined, MailOutlined, PhoneOutlined, SaveOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_ROUTES from '../../api';
import { syncProfileToLocalStorage } from '../../utils/authProfile';
import './index.css';

const { Title, Text } = Typography;

const buildFileList = (url, name) => {
    if (!url || url === 'null') {
        return [];
    }
    return [{
        uid: url,
        name,
        status: 'done',
        url,
    }];
};

export default function Profile() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState({});
    const [proofFileList, setProofFileList] = useState([]);
    const [proofUrl, setProofUrl] = useState();

    const syncProfileState = useCallback((data) => {
        syncProfileToLocalStorage(data || {});
        setProfile(data || {});
        setProofUrl(data?.studentCertificate);
        setProofFileList(buildFileList(data?.studentCertificate, 'Affiliation Verification Document'));
        form.setFieldsValue({
            email: data?.email,
            realName: data?.realName,
            org: data?.org,
            mobile: data?.mobile,
        });
    }, [form]);

    const loadProfile = useCallback(() => {
        setLoading(true);
        axios.get(API_ROUTES.api_profile, {
            headers: {
                Authorization: localStorage.getItem('userInfo'),
            },
        }).then((response) => {
            if (response.data.code === 200) {
                syncProfileState(response.data.data);
            } else {
                Modal.error({ content: response.data.msg || 'Failed to load profile.' });
            }
        }).catch(() => {
            navigate('/login');
        }).finally(() => {
            setLoading(false);
        });
    }, [navigate, syncProfileState]);

    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (!userInfo) {
            navigate('/login');
            return;
        }
        loadProfile();
    }, [loadProfile, navigate]);

    const beforeImageUpload = (file) => {
        const isImage = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type);
        if (!isImage) {
            messageApi.error('Only image files are allowed.');
        }
        return isImage || Upload.LIST_IGNORE;
    };

    const createUploadHandler = (setter) => async ({ file, onSuccess, onError }) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axios.post(API_ROUTES.api_profile_upload, formData, {
                headers: {
                    Authorization: localStorage.getItem('userInfo'),
                },
            });
            if (response.data.code === 200) {
                // 上传完成后先保存图片地址，最终由保存按钮统一提交资料
                setter(response.data.data.uploadFile);
                onSuccess('ok');
            } else {
                messageApi.error(response.data.msg || 'Upload failed.');
                onError(new Error(response.data.msg || 'Upload failed.'));
            }
        } catch (error) {
            messageApi.error('Upload failed.');
            onError(error);
        }
    };

    const saveProfile = (values) => {
        if (Number(profile.manage) !== 1) {
            Modal.confirm({
                title: 'Submit profile changes?',
                content: 'After updating your personal information, your account will require manual review again before Imputation and Variant Search can be used.',
                okText: 'Submit changes',
                cancelText: 'Cancel',
                onOk: () => submitProfile(values),
            });
            return;
        }
        submitProfile(values);
    };

    const submitProfile = (values) => {
        setSaving(true);
        axios.post(API_ROUTES.api_profile_update, {
            realName: values.realName,
            org: values.org,
            mobile: values.mobile,
            studentCertificate: proofUrl,
        }, {
            headers: {
                Authorization: localStorage.getItem('userInfo'),
            },
        }).then((response) => {
            if (response.data.code === 200) {
                const data = response.data.data;
                syncProfileState(data);
                // 个人资料更新后统一同步本地审核状态缓存
                syncProfileToLocalStorage(data);
                messageApi.success('Profile updated successfully.');
            } else {
                messageApi.error(response.data.msg || 'Profile update failed.');
            }
        }).catch(() => {
            messageApi.error('Profile update failed.');
        }).finally(() => {
            setSaving(false);
        });
    };

    const uploadProps = (fileList, setFileList, setter) => ({
        listType: 'picture-card',
        accept: 'image/png,image/jpeg,image/jpg,image/webp',
        maxCount: 1,
        fileList,
        beforeUpload: beforeImageUpload,
        customRequest: createUploadHandler(setter),
        onChange: ({ fileList: nextFileList }) => setFileList(nextFileList),
    });

    return (
        <div className="profile-page">
            {contextHolder}
            <section className="profile-hero">
                <Avatar size={72} icon={<UserOutlined />} className="profile-avatar" />
                <Title level={1}>Personal Profile</Title>
                <Text>Review and update your account information.</Text>
            </section>

            <Card className="profile-card" loading={loading}>
                <div className="profile-card-header">
                    <div>
                        <Title level={3}>Account information</Title>
                        <Text>Keep your institutional information up to date.</Text>
                    </div>
                    <Tag color={Number(profile.status) === 1 ? 'success' : 'warning'}>
                        {Number(profile.status) === 1 ? 'Active' : 'Pending review'}
                    </Tag>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={saveProfile}
                    className="profile-form"
                >
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Email Address" name="email">
                                <Input prefix={<MailOutlined />} disabled />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Full Name"
                                name="realName"
                                rules={[{ required: true, message: 'Please enter your full name.' }]}
                            >
                                <Input prefix={<UserOutlined />} placeholder="Your legal name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Organization / University"
                                name="org"
                                rules={[{ required: true, message: 'Please enter your organization.' }]}
                            >
                                <Input prefix={<BankOutlined />} placeholder="Current institution" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Contact Number" name="mobile">
                                <Input prefix={<PhoneOutlined />} placeholder="e.g., +86 138..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="profile-upload-section">
                        <div className="profile-upload-title">
                            <IdcardOutlined />
                            <span>Verification documents</span>
                        </div>
                        <Row gutter={[24, 24]}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Affiliation Verification Document">
                                    <Upload {...uploadProps(proofFileList, setProofFileList, setProofUrl)}>
                                        <div className="profile-upload-trigger">
                                            <UploadOutlined />
                                            <span>Upload Proof</span>
                                        </div>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="profile-actions">
                        <Button onClick={loadProfile}>Reset</Button>
                        <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                            Save profile
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
